import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Avatar,
  Button,
  Textarea,
  Chip,
  Divider
} from '@heroui/react';
import {
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  Edit3,
  Save,
  X,
  MoreHorizontal,
  Star
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useStomp } from '../../contexts/StompContext';
import CommentCreator from './CommentCreator';

const Comment = ({ comment, post, onCommentUpdate, onReplyAdded, parentComment = null, level = 0 }) => {
  const { user } = useAuth();
  const { addMessageHandler, isConnected } = useStomp();
  const [upvotes, setUpvotes] = useState(comment.upvotes || 0);
  const [downvotes, setDownvotes] = useState(comment.downvotes || 0);
  const [userVote, setUserVote] = useState(comment.myvote?.direction || null);
  const [isVoting, setIsVoting] = useState(false);
  const [showReplyCreator, setShowReplyCreator] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text || '');
  const [replies, setReplies] = useState(comment.replies || []);
  const [showMoreReplies, setShowMoreReplies] = useState(false);
  const [replyPage, setReplyPage] = useState(1);
  const [hasMoreReplies, setHasMoreReplies] = useState(
    comment.comment_total > (comment.replies?.length || 0)
  );
  const [loadingReplies, setLoadingReplies] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);

  const isOwner = user && user.username === comment.user?.username;
  const maxLevel = 3; // Maximum nesting level for replies

  // STOMP real-time vote updates for this comment
  useEffect(() => {
    if (!isConnected) return;

    const commentId = comment._id?.['$oid'] || comment._id;

    // Handle vote updates for this specific comment
    const handleVoteUpdate = (message, topic) => {
      let messageCommentId = null;
      let voteData = null;

      // Handle the vote_update format
      if (message.vote_update?.comment) {
        messageCommentId = message.vote_update.comment._id?.['$oid'] || message.vote_update.comment._id;
        voteData = {
          upvotes: message.vote_update.upvotes,
          downvotes: message.vote_update.downvotes,
          new_direction: message.vote_update.new_direction,
          voter: message.vote_update.voter
        };
      }
      if (messageCommentId === commentId && voteData) {
        console.log('Comment: Received vote update for this comment', commentId);
        console.log('Comment: Vote data:', voteData);
        // Always update vote counts from authoritative server data
        setUpvotes(voteData.upvotes);
        setDownvotes(voteData.downvotes);
        // Update user vote state if this is the current user's vote
        if (voteData.voter && user && voteData.voter.username === user.username) {
          setUserVote(voteData.new_direction);
          setIsVoting(false); // Re-enable voting now that our vote is confirmed
          console.log('Comment: Updated user vote state to:', voteData.new_direction);
        }
      }
    };

    // Register handler
    const removeVoteHandler = addMessageHandler('vote', handleVoteUpdate);

    console.log('Comment: Registered STOMP vote handler for comment', commentId);

    // Cleanup
    return () => {
      removeVoteHandler();
      console.log('Comment: Removed STOMP vote handler for comment', commentId);
    };
  }, [isConnected, addMessageHandler]); // Remove comment._id and user as they shouldn't change for the same comment instance

  // Role badge component
  const getRoleBadge = (userRole) => {
    if (!userRole) return null;
    const roleConfig = {
      'Fundador': { color: 'warning', icon: '👑', label: 'Fundador' },
      'Moderador': { color: 'primary', icon: '⭐', label: 'Mod' },
      'Admin': { color: 'danger', icon: '🛡️', label: 'Admin' }
    };

    const config = roleConfig[userRole.name];
    if (!config) return null;

    return (
      <Chip
        size="sm"
        color={config.color}
        variant="flat"
        startContent={<span className="text-xs">{config.icon}</span>}
        className="ml-2 h-5 text-xs"
      >
        {config.label}
      </Chip>
    );
  };

  const handleVote = async (voteType) => {
    if (isVoting) return;

    setIsVoting(true);

    try {
      const postId = post._id?.['$oid'] || post._id;
      const commentId = comment._id?.['$oid'] || comment._id;

      let endpoint, method;

      if (userVote === voteType) {
        // Remove vote
        endpoint = `/api/v1/posts/${postId}/vote`;
        method = 'DELETE';
      } else {
        // Add or change vote
        endpoint = `/api/v1/posts/${postId}/comments/${commentId}/${voteType}vote`;
        method = 'PUT';
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        ...(method === 'DELETE' && { body: JSON.stringify({ comment_id: commentId }) })
      });

      if (response.ok) {
        console.log(`Comment: Vote API call successful, waiting for STOMP update`);
        // No optimistic updates - wait for STOMP message to update UI
      } else {
        console.error(`Failed to ${voteType}vote comment`);
        setIsVoting(false); // Re-enable voting on error
      }
    } catch (error) {
      console.error(`Error ${voteType}voting comment:`, error);
      setIsVoting(false); // Re-enable voting on error
    }
    // Note: setIsVoting(false) will be called when STOMP message arrives
  };

  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja deletar este comentário?')) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/v1/posts/${post._id}/comments/${comment._id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Update comment text to show as deleted
        const updatedComment = { ...comment, text: '[Deleted]' };
        onCommentUpdate(updatedComment);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditText(comment.text || '');
  };

  const handleSaveEdit = async () => {
    try {
      const postId = post._id?.['$oid'] || post._id;
      const commentId = comment._id?.['$oid'] || comment._id;

      const response = await fetch(`/api/v1/posts/${postId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          comment: {
            text: editText
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        comment.text = editText;
        setIsEditing(false);
        if (onCommentUpdate) {
          onCommentUpdate(data.comment);
        }
      } else {
        console.error('Failed to update comment');
        alert('Erro ao atualizar comentário');
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      alert('Erro ao atualizar comentário');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(comment.text || '');
  };

  const handleReply = () => {
    if (level >= maxLevel) {
      // If at max level, add reply to parent instead
      if (parentComment) {
        parentComment.setShowReplyCreator(true);
      }
    } else {
      setShowReplyCreator(true);
    }
  };

  const handleReplyAdded = (newReply) => {
    setReplies(prev => [...prev, newReply]);
    setShowReplyCreator(false);
    if (onReplyAdded) {
      onReplyAdded(newReply);
    }
  };

  const loadMoreReplies = async () => {
    if (loadingReplies || !hasMoreReplies) return;

    setLoadingReplies(true);

    try {
      const postId = post._id?.['$oid'] || post._id;
      const commentId = comment._id?.['$oid'] || comment._id;
      const nextPage = replyPage + 1;

      const response = await fetch(`/api/v1/posts/${postId}/comments?comment_id=${commentId}&comment_page=${nextPage}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        const newReplies = data.comments || [];

        setReplies(prev => [...prev, ...newReplies]);
        setReplyPage(nextPage);
        setHasMoreReplies(newReplies.length === 5); // COMMENT_PAGE = 5
      } else {
        console.error('Failed to load more replies');
      }
    } catch (error) {
      console.error('Error loading more replies:', error);
    } finally {
      setLoadingReplies(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'agora';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d`;

    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className={`${level > 0 ? 'ml-8 mt-3' : 'mt-4'}`}>
      <Card className="shadow-sm rounded-nextui bg-white border border-gray-200">
        <CardBody className="p-4">
          {/* Comment Header */}
          <div className="flex items-start space-x-3">
            <Avatar
              src={comment.user?.avatar}
              name={comment.user?.username}
              size="sm"
              className="flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2">
                <span className="font-semibold text-sm text-gray-900">
                  {comment.user?.username}
                </span>
                {getRoleBadge(comment.user?.role)}
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(comment.created_at)}
                </span>
              </div>
              {/* Comment Content */}
              {isEditing ? (
                <div className="space-y-3">
                  <Textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    placeholder="Edite seu comentário..."
                    minRows={2}
                    size="sm"
                  />
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      color="primary"
                      startContent={<Save className="w-4 h-4" />}
                      onPress={handleSaveEdit}
                      isDisabled={!editText.trim()}
                    >
                      Salvar
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      startContent={<X className="w-4 h-4" />}
                      onPress={handleCancelEdit}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
                  {comment.text}
                </div>
              )}
              {/* Comment Actions */}
              {!isEditing && (
                <div className="flex items-center space-x-4 text-xs">
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() => handleVote('up')}
                    isDisabled={isVoting}
                    className={`h-6 px-2 ${userVote === 'up' ? 'text-blue-600 bg-blue-50' : 'text-gray-500'}`}
                  >
                    {!isVoting && <ThumbsUp className="w-3 h-3 mr-1" />}
                    {upvotes}
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    onPress={() => handleVote('down')}
                    isDisabled={isVoting}
                    className={`h-6 px-2 ${userVote === 'down' ? 'text-red-600 bg-red-50' : 'text-gray-500'}`}
                  >
                    {!isVoting && <ThumbsDown className="w-3 h-3 mr-1" />}
                    {downvotes}
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    startContent={<MessageCircle className="w-3 h-3" />}
                    onPress={handleReply}
                    className="h-6 px-2 text-gray-500"
                  >
                    Responder
                  </Button>
                  {isOwner && (
                    <>
                      <Button
                        size="sm"
                        variant="light"
                        startContent={<Edit3 className="w-3 h-3" />}
                        onPress={handleEdit}
                        className="h-6 px-2 text-gray-500"
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="light"
                        startContent={<X className="w-3 h-3" />}
                        onPress={handleDelete}
                        isLoading={isDeleting}
                        className="h-6 px-2 text-red-500"
                      >
                        Deletar
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
      {/* Reply Creator */}
      {showReplyCreator && (
        <div className="mt-3 ml-8">
          <CommentCreator
            post={post}
            parentCommentId={comment._id?.['$oid'] || comment._id}
            onCommentAdded={handleReplyAdded}
            onCancel={() => setShowReplyCreator(false)}
            placeholder="Escreva uma resposta..."
          />
        </div>
      )}
      {/* Replies */}
      {replies.length > 0 && (
        <div className="mt-2">
          {replies.map((reply) => (
            <Comment
              key={reply._id?.['$oid'] || reply._id}
              comment={reply}
              post={post}
              onCommentUpdate={onCommentUpdate}
              onReplyAdded={onReplyAdded}
              parentComment={{ setShowReplyCreator }}
              level={level + 1}
            />
          ))}
        </div>
      )}
      {/* Load More Replies */}
      {hasMoreReplies && (
        <div className="mt-2 ml-8">
          <button
            onClick={loadMoreReplies}
            disabled={loadingReplies}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
          >
            {loadingReplies ? 'Carregando...' : `Ver mais ${comment.comment_total - replies.length} respostas`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Comment;
